import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Grid,
  Group,
  Modal,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { useHotkeys, useWindowEvent } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconDeviceFloppy, IconWorld } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useBlocker, useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '@/api/admin';
import { errorMessage, isConflict } from '@/api/http';
import { qk, takeOwnProgrammeWrite, useProgramme, useSetProgramme } from '@/api/queries';
import type { AdminProgramme } from '@/api/types';
import { useCan } from '@/auth/useCan';
import { QueryState } from '@/components/QueryState';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDateTime, isFuture } from '@/lib/format';
import { ArtworkTab } from './editor/ArtworkTab';
import { ClassificationTab } from './editor/ClassificationTab';
import { DetailsTab } from './editor/DetailsTab';
import { EpisodesTab } from './editor/EpisodesTab';
import { ProgrammeFormProvider, useProgrammeForm } from './editor/formContext';
import { buildPatch, TABS, toFormValues, validateForm, type EditorTab } from './editor/model';
import { PreviewTab } from './editor/PreviewTab';
import { RightsTab } from './editor/RightsTab';
import { ValidationChecklist } from './editor/ValidationChecklist';
import { WorkflowBar } from './editor/WorkflowBar';

export function ProgrammeEditorPage() {
  const { id = '' } = useParams();
  const query = useProgramme(id);
  return (
    <QueryState query={query} loadingLabel="Loading programme…">
      {(p) => <ProgrammeEditor key={p.id} programme={p} />}
    </QueryState>
  );
}

function ProgrammeEditor({ programme }: { programme: AdminProgramme }) {
  const { tab: tabParam } = useParams();
  const tab: EditorTab = TABS.some((t) => t.value === tabParam)
    ? (tabParam as EditorTab)
    : 'details';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setProgramme = useSetProgramme();
  const canEdit = useCan('edit');

  const form = useProgrammeForm({
    mode: 'controlled',
    initialValues: toFormValues(programme),
    validate: (values) => validateForm(values),
  });
  // The revision our edits are based on (sent as If-Match).
  const [baseRevision, setBaseRevision] = useState(programme.revision);
  const [conflict, setConflict] = useState<string | null>(null);
  const dirty = form.isDirty();

  // Adopt server changes (e.g. after episode edits or a transition) when there's nothing local
  // to lose. With local edits, keep them: if the change was our own episode/video/workflow
  // action, rebase on its revision (saves send only changed fields); otherwise a later save
  // surfaces the conflict.
  useEffect(() => {
    const ownWrite = takeOwnProgrammeWrite(programme.id);
    if (!form.isDirty()) {
      const values = toFormValues(programme);
      form.setInitialValues(values);
      form.setValues(values);
      form.resetDirty(values);
      setBaseRevision(programme.revision);
    } else if (ownWrite) {
      setBaseRevision(programme.revision);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programme]);

  const save = useMutation({
    mutationFn: () =>
      adminApi.updateProgramme(
        programme.id,
        baseRevision,
        buildPatch(form.getInitialValues(), form.getValues()),
      ),
    onSuccess: (p) => {
      const values = toFormValues(p);
      form.setInitialValues(values);
      form.setValues(values);
      form.resetDirty(values);
      setBaseRevision(p.revision);
      setProgramme(p);
      notifications.show({ color: 'leaf', message: 'Saved.' });
    },
    onError: (e) => {
      if (isConflict(e)) setConflict(errorMessage(e));
      else notifications.show({ color: 'coral', title: 'Not saved', message: errorMessage(e) });
    },
  });

  const doSave = useCallback(() => {
    if (!form.isDirty() || save.isPending) return;
    if (form.validate().hasErrors) {
      notifications.show({ color: 'coral', message: 'Fix the highlighted fields first.' });
      return;
    }
    save.mutate();
  }, [form, save]);

  const discard = () => form.reset();

  const reloadLatest = async () => {
    setConflict(null);
    const latest = await qc.fetchQuery({
      queryKey: qk.programme(programme.id),
      queryFn: () => adminApi.getProgramme(programme.id),
      staleTime: 0,
    });
    const values = toFormValues(latest);
    form.setInitialValues(values);
    form.setValues(values);
    form.resetDirty(values);
    setBaseRevision(latest.revision);
  };

  const keepEditing = async () => {
    setConflict(null);
    // Rebase on the latest revision; only the fields changed here are sent on the next save.
    const latest = await qc.fetchQuery({
      queryKey: ['programme-latest', programme.id],
      queryFn: () => adminApi.getProgramme(programme.id),
      staleTime: 0,
    });
    setBaseRevision(latest.revision);
  };

  useHotkeys([['mod+S', doSave]], []);
  useWindowEvent('beforeunload', (e) => {
    if (form.isDirty()) {
      e.preventDefault();
      e.returnValue = '';
    }
  });
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      form.isDirty() &&
      !nextLocation.pathname.startsWith(`/programmes/${programme.id}`) &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  const isLive = programme.status === 'published';
  const scheduled = programme.scheduled;

  return (
    <ProgrammeFormProvider form={form}>
      <Stack gap="sm" pb={dirty ? 72 : 0}>
        <Breadcrumbs>
          <Anchor component={Link} to="/programmes" size="sm">
            Programmes
          </Anchor>
          <Text size="sm">{programme.title}</Text>
        </Breadcrumbs>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={4}>
            <Group gap="sm">
              <Title order={1}>{programme.title}</Title>
              <StatusBadge status={programme.status} scheduled={scheduled} />
            </Group>
            <Text size="xs" c="dimmed">
              {programme.kind === 'series' ? 'Series' : 'Standalone'} · revision{' '}
              {programme.revision} · updated {formatDateTime(programme.updated_at)}
              {programme.updated_by ? ` by ${programme.updated_by}` : ''}
            </Text>
          </Stack>
          <WorkflowBar programme={programme} dirty={dirty} />
        </Group>

        {isLive && (
          <Alert
            color="leaf"
            variant="light"
            icon={<IconWorld size={18} />}
            title={scheduled ? 'This is live (scheduled)' : 'This is live'}
          >
            {isFuture(programme.published_at)
              ? `Children will see it from ${formatDateTime(programme.published_at)}. `
              : 'Children can see this programme now. '}
            {programme.unpublish_at &&
              `It comes down at ${formatDateTime(programme.unpublish_at)}. `}
            Changes you save apply to the app straight away.
          </Alert>
        )}
        {programme.status === 'approved' && (
          <Alert color="sky" variant="light">
            Approved and waiting to be published. Saving any change sends it back to review.
          </Alert>
        )}

        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 8, xl: 9 }} order={{ base: 2, md: 1 }}>
            <Tabs
              value={tab}
              onChange={(v) =>
                navigate(`/programmes/${programme.id}/${v ?? 'details'}`, { replace: true })
              }
              keepMounted={false}
            >
              <Tabs.List mb="md" style={{ flexWrap: 'wrap' }}>
                {TABS.map((t) => (
                  <Tabs.Tab key={t.value} value={t.value}>
                    {t.label}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              <fieldset
                disabled={!canEdit}
                style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
              >
                <Tabs.Panel value="details">
                  <DetailsTab programme={programme} />
                </Tabs.Panel>
                <Tabs.Panel value="artwork">
                  <ArtworkTab programme={programme} />
                </Tabs.Panel>
                <Tabs.Panel value="classification">
                  <ClassificationTab />
                </Tabs.Panel>
                <Tabs.Panel value="episodes">
                  <EpisodesTab programme={programme} />
                </Tabs.Panel>
                <Tabs.Panel value="rights">
                  <RightsTab />
                </Tabs.Panel>
                <Tabs.Panel value="preview">
                  <PreviewTab programme={programme} />
                </Tabs.Panel>
              </fieldset>
            </Tabs>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4, xl: 3 }} order={{ base: 1, md: 2 }}>
            <Box pos={{ base: 'static', md: 'sticky' }} top={64}>
              <ValidationChecklist programme={programme} dirty={dirty} />
            </Box>
          </Grid.Col>
        </Grid>
      </Stack>

      {dirty && (
        <Paper
          withBorder
          shadow="md"
          p="sm"
          radius={0}
          className="pb-surface"
          style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 150 }}
          role="region"
          aria-label="Unsaved changes"
        >
          <Group justify="flex-end" gap="sm" maw={1400} mx="auto" px="md">
            <Text size="sm" c="dimmed" mr="auto">
              {isLive
                ? 'Unsaved changes — saving updates the live programme.'
                : 'You have unsaved changes.'}
            </Text>
            <Button variant="default" onClick={discard} disabled={save.isPending}>
              Discard
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={doSave}
              loading={save.isPending}
              disabled={!canEdit}
            >
              Save changes
            </Button>
          </Group>
        </Paper>
      )}

      <Modal
        opened={!!conflict}
        onClose={() => void keepEditing()}
        title="Someone else changed this programme"
        closeOnClickOutside={false}
      >
        <Stack>
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <IconAlertTriangle size={20} color="var(--pb-warning)" aria-hidden />
            <Text size="sm">{conflict} Your changes haven’t been saved.</Text>
          </Group>
          <Text size="sm" c="dimmed">
            <b>Reload latest</b> shows their version and discards your edits. <b>Keep editing</b>{' '}
            keeps your edits here; saving again applies just the fields you changed on top of their
            version.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => void reloadLatest()}>
              Reload latest
            </Button>
            <Button onClick={() => void keepEditing()}>Keep editing</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        title="Leave without saving?"
      >
        <Stack>
          <Text size="sm">
            You have unsaved changes to {programme.title}. They’ll be lost if you leave.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => blocker.reset?.()}>
              Stay
            </Button>
            <Button color="coral" onClick={() => blocker.proceed?.()}>
              Leave and discard
            </Button>
          </Group>
        </Stack>
      </Modal>
    </ProgrammeFormProvider>
  );
}
